// src/services/DepartmentLayoutService.js
//
// Where each department box sits on the display.
//
// Not an order: a placement. A flat order lets the packer choose which column a
// box lands in, so moving one department shifts everything after it into
// different columns. Column and position together mean the box stays put.
//
// Rows are deliberately absent. A team of four is a short box and a team of
// forty is a tall one, so one column may hold six boxes and another two. There
// is no grid to fit into.

import apiClient from './apiClient'

export async function getDepartmentLayout(countryCode) {
  const response = await apiClient.post('/GetDepartmentLayout', { country_code: countryCode })

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not read the layout')
  }

  return {
    columns: response.data.columns ?? 6,
    placements: response.data.placements ?? []
  }
}

export async function setDepartmentLayout(countryCode, columns, placements) {
  const response = await apiClient.post('/SetDepartmentLayout', {
    country_code: countryCode,
    columns,
    placements
  })

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not save the layout')
  }
}

/**
 * Arrange departments into columns using a saved layout.
 *
 * Placed departments go where they were put. Anything unplaced -- a team that
 * appeared in Entra since the layout was made -- is appended to the shortest
 * column, so it shows up rather than vanishing, without disturbing the
 * arrangement.
 *
 * Returns an array of columns, each an array of departments.
 */
export function applyLayout(departments, columns, placements) {
  const cols = Array.from({ length: Math.max(1, columns) }, () => [])

  const placedBy = new Map()
  for (const p of placements ?? []) placedBy.set(p.department, p)

  const unplaced = []

  for (const d of departments) {
    const at = placedBy.get(d.name)
    if (!at) { unplaced.push(d); continue }

    const index = Math.min(Math.max(at.column ?? 0, 0), cols.length - 1)
    cols[index].push({ ...d, position: at.position ?? 0 })
  }

  for (const col of cols) col.sort((a, b) => a.position - b.position)

  // Shortest column by box count rather than by height: this runs before
  // anything is measured, and a rough answer that puts the box somewhere
  // sensible beats waiting for a precise one.
  for (const d of unplaced.sort((a, b) => a.name.localeCompare(b.name))) {
    let shortest = 0
    for (let i = 1; i < cols.length; i++) {
      if (cols[i].length < cols[shortest].length) shortest = i
    }
    cols[shortest].push(d)
  }

  return cols
}

/** Columns of departments back into the flat placement list the API takes. */
export function toPlacements(cols) {
  const out = []
  cols.forEach((col, column) => {
    col.forEach((d, position) => {
      out.push({ department: d.name, column, position })
    })
  })
  return out
}
